import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { extractUserListContext, userQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { CreateUserRequest } from "@/types/request/CreateUserRequest";
import { ValidationResponse } from "@/types/response/ValidationResponse";

type UseCreateUserMutationParams = {
  tenantId?: string | null;
  clientId?: string | null;
};

/**
 * Hook to create a new user.
 *
 * @param params - Parameters for the mutation
 * @param params.tenantId - Tenant ID to associate the user with
 * @param params.clientId - Client ID to associate the user with
 *
 * @returns Mutation object for creating a user
 *
 * @example
 * ```tsx
 * const createUser = useCreateUserMutation({ tenantId: "123", clientId: "456" });
 * createUser.mutate({ email: "user@example.com", firstName: "John", ... });
 * ```
 */
export function useCreateUserMutation(params?: UseCreateUserMutationParams) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ValidationResponse>,
    CreateUserRequest
  >({
    mutationFn: (createUserRequest: CreateUserRequest) =>
      instance.post("/api/v2/Users", {
        ...createUserRequest,
        tenantId: params?.tenantId,
        clientId: params?.clientId,
      }),
    onSuccess: () => {
      showToast(t("success.created", { entity: "User" }), "success");

      // Refetch user list so overview updates immediately (bypasses staleTime)
      if (params?.clientId) {
        queryClient.refetchQueries({
          queryKey: userQueryKeys.lists(),
          predicate: (query) => {
            const context = extractUserListContext(query.queryKey);
            return context.clientId === params.clientId;
          },
        });
      } else if (params?.tenantId) {
        queryClient.refetchQueries({
          queryKey: userQueryKeys.lists(),
          predicate: (query) => {
            const context = extractUserListContext(query.queryKey);
            return (
              context.tenantId === params.tenantId &&
              (context.clientId === null || context.clientId === undefined)
            );
          },
        });
      } else {
        queryClient.refetchQueries({
          queryKey: userQueryKeys.lists(),
        });
      }
    },
  });
}
